"use client";
import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";
import CheckIcon from "@mui/icons-material/Check";
import { IMAGES } from "../constants/images";
import NextLink from "next/link";
const CTASectionAIAgent = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const badges = ["badge1", "badge2", "badge3", "badge4"];

  return (
    <Box
      sx={{
        backgroundImage: `url("https://getgeek-website-assets.b-cdn.net/CTASectionAIAgentNew.webp")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        py: { xs: 6, sm: 8, md: 14 },
        px: { xs: 1.5, sm: 2, md: 2 },
        textAlign: "center",
      }}
    >
      <Container maxWidth="md">
        {/* Heading */}
        <Typography
          sx={{
            fontFamily: "var(--font-plusjakarta)",
            fontWeight: 700,
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.9rem" },
            color: "#fff",
            lineHeight: { xs: 1.3, sm: 1.25, md: 1.2 },
            mb: 0.5,
          }}
        >
          {t("cta.line1")}{" "}
          <Box component="span" sx={{ color: "#C99BFF" }}>
            {t("cta.line2")}
          </Box>
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontSize: { xs: "0.82rem", sm: "0.9rem", md: "1rem" },
            color: "#B8B3C4",
            lineHeight: { xs: 1.6, sm: 1.55, md: 1.5 },
            maxWidth: { xs: "100%", sm: 480, md: 590 },
            mx: "auto",
            mt: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 3, sm: 4, md: 5 },
            px: { xs: 1, sm: 0 },
          }}
        >
          {t("cta.subtitle")}
        </Typography>

        {/* Bold tagline */}
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontWeight: 600,
            fontSize: { xs: "0.88rem", sm: "1rem", md: "1.15rem" },
            color: "#fff",
            lineHeight: { xs: 1.7, sm: 1.65, md: 1.6 },
            mb: { xs: 3, sm: 3.5, md: 4 },
            px: { xs: 1, sm: 0 },
          }}
        >
          {t("cta.tagline1")}{" "}
          {t("cta.tagline2")}
        </Typography>

        {/* CTA Button */}
        <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
          <Button
            component="a"
            rel="noopener noreferrer"
            sx={{
              background:
                "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 50%, #6D28D9 100%)",
              color: "#fff",
              fontFamily: "var(--font-outfit)",
              fontWeight: 500,
              fontSize: { xs: "0.82rem", sm: "0.9rem", md: "1rem" },
              px: { xs: 2.5, sm: 3.5, md: 4.5 },
              py: { xs: 1.4, sm: 1.6, md: 1.8 },
              borderRadius: "50px",
              textTransform: "none",
              mb: { xs: 3, sm: 3.5, md: 4 },
              transition: "all 0.3s ease",
              // boxShadow: "0px 0px 30px rgba(139, 92, 246, 0.5)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #9333ea 0%, #7c3aed 50%, #6d28d9 100%)",
                transform: "scale(1.03)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            {t("cta.button")}
          </Button>
        </NextLink>

        {/* Trust Badges */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 0.5, sm: 0 },
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {badges.map((key) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.7,
                border: "1px solid rgba(0,201,80,0.4)",
                borderRadius: "50px",
                px: { xs: 1.4, sm: 1.7, md: 2 },
                py: { xs: 0.6, sm: 0.7, md: 0.8 },
                backgroundColor: "rgba(255,255,255,0.03)",
              }}
            >
              <CheckIcon
                sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#00C950" }}
              />
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.68rem", sm: "0.72rem", md: "0.82rem" },
                  color: "#E5E7EB",
                  whiteSpace: "nowrap",
                }}
              >
                {t(`cta.${key}`)}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Closing block */}
        <Typography
          sx={{
            fontFamily: "var(--font-plusjakarta)",
            fontWeight: 700,
            fontSize: { xs: "0.95rem", sm: "1.15rem", md: "1.4rem" },
            color: "#fff",
            lineHeight: 1.4,
            mb: { xs: 1, sm: 1.25, md: 1.5 },
          }}
        >
          {t("cta.closingTitle")}
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--font-outfit)",
            fontSize: { xs: "0.75rem", sm: "0.82rem", md: "0.92rem" },
            color: "#9CA3AF",
            lineHeight: 1.6,
            maxWidth: { xs: "100%", sm: 480, md: 560 },
            mx: "auto",
            px: { xs: 1, sm: 0 },
          }}
        >
          {t("cta.closingSubtitle")}
        </Typography>
      </Container>
    </Box>
  );
};

export default CTASectionAIAgent;